import React from "react";
import { create } from "zustand";
import { useLocalStorage } from "react-use";

import { useBreakpoint } from "@puzzle/theme";
import { Sidepanel } from "@puzzle/icons";

import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";

import { MINIMIZED_SIDEBAR } from "constants/localStorageKeys";
import { buttonStyle } from "./minimizeSidebarButton.css";

type NavSidebarState = {
  isNavMinimized: boolean;
  setIsNavMinimized: (val: boolean) => void;
};

const getInitialState = () => {
  try {
    const stored = localStorage.getItem(MINIMIZED_SIDEBAR);
    return stored ? JSON.parse(stored) : false;
  } catch {
    return false;
  }
};

const useNavSidebarStore = create<NavSidebarState>((set) => ({
  isNavMinimized: getInitialState(),
  setIsNavMinimized: (val) => set({ isNavMinimized: val }),
}));

export const useNavSidebarState = () => {
  const [minimizedLocal = false, setMinimizedLocal] = useLocalStorage<boolean>(
    MINIMIZED_SIDEBAR,
    false
  );
  const { isNavMinimized: isNavMinimizedStore, setIsNavMinimized: setIsNavMinimizedStore } =
    useNavSidebarStore();

  const setIsNavMinimized = (val: boolean) => {
    setMinimizedLocal(val);
    setIsNavMinimizedStore(val);
  };

  return {
    isNavMinimized: isNavMinimizedStore,
    setIsNavMinimized,
  };
};

export const MinimizeSidebarButton = () => {
  const breakpoint = useBreakpoint();
  const { isNavMinimized, setIsNavMinimized } = useNavSidebarState();

  const isPartialMinimizedEnabled = isPosthogFeatureFlagEnabled(
    FeatureFlag.SidebarPartialMinimized
  );
  const showButton = isNavMinimized || ["xs", "s", "m"].includes(breakpoint);

  if (!showButton || isPartialMinimizedEnabled) {
    // when partial minimization is enabled,
    // we don't need to show this as the minimize
    // action will be in the sidenav
    return null;
  }

  return (
    <button className={buttonStyle} onClick={() => setIsNavMinimized(!isNavMinimized)}>
      <Sidepanel />
    </button>
  );
};
